import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  IndianRupee, Lock, Unlock, Save, Loader2, AlertTriangle, Check,
  Sparkles, ArrowLeft,
} from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

/**
 * ProjectRateCardPage — locked-once price list per project.
 *
 * Founder spec (May 14, 2026):
 *   "We must have a PRICE LIST set for the project. Once locked, when I
 *    ALLOT shots/assets/rigs the Work Order auto-populates."
 *
 * Layout:
 *   [project picker]
 *   [global lock banner if locked]
 *   [editable rate table — one row per department]
 *   [Save / Lock / Unlock buttons]
 *
 * Locked semantics: only master_admin can lock/unlock. PMs can edit
 * when unlocked. Other roles see a read-only view.
 */
const DEPT_ROWS = [
  { key: 'concept_art', label: 'Concept Art',   unit: 'asset',  group: 'Pre-Production' },
  { key: 'storyboard',  label: 'Storyboard',    unit: 'asset',  group: 'Pre-Production' },
  { key: 'animatics',   label: 'Animatics',     unit: 'minute', group: 'Pre-Production' },
  { key: 'cba',         label: 'Cut By Animatics', unit: 'minute', group: 'Pre-Production' },
  { key: 'asset_build', label: 'Asset Build',   unit: 'asset',  group: 'Asset' },
  { key: 'modeling',    label: 'Modeling',      unit: 'asset',  group: 'Asset' },
  { key: 'rigging',     label: 'Rigging',       unit: 'asset',  group: 'Asset' },
  { key: 'layout',      label: 'Layout',        unit: 'second', group: 'Animation' },
  { key: 'animation',   label: 'Animation',     unit: 'second', group: 'Animation' },
  { key: 'lighting',    label: 'Lighting',      unit: 'shot',   group: 'Post' },
  { key: 'render',      label: 'Rendering',     unit: 'shot',   group: 'Post' },
  { key: 'fx',          label: 'FX',            unit: 'shot',   group: 'Post' },
  { key: 'compositing', label: 'Compositing',   unit: 'shot',   group: 'Post' },
  { key: 'sound',       label: 'Sound',         unit: 'minute', group: 'Post' },
  { key: 'lrc',         label: 'LRC / Final QC',unit: 'shot',   group: 'Post' },
];

export default function ProjectRateCardPage() {
  const { api, user } = useAuth();
  const params = useParams();
  const urlProjectId = params.projectId || '';
  const isAdmin = ['master_admin', 'production_manager', 'vendor_manager', 'coo'].includes(user?.role);
  const isMasterAdmin = user?.role === 'master_admin';

  const [projects, setProjects] = useState([]);
  const [projectId, setProjectId] = useState(urlProjectId);
  const [card, setCard] = useState(null);
  const [rates, setRates] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lockBusy, setLockBusy] = useState(false);
  const [saveOk, setSaveOk] = useState(false);

  useEffect(() => {
    api.get('/projects').then(r => {
      const list = Array.isArray(r.data) ? r.data : (r.data?.projects || []);
      setProjects(list);
      if (!projectId && list.length) setProjectId(urlProjectId || list[0].id);
    }).catch(() => setProjects([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api]);

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const r = await api.get(`/projects/${projectId}/rate-card`);
      setCard(r.data);
      const merged = {};
      DEPT_ROWS.forEach(row => {
        const incoming = r.data.rates?.[row.key];
        merged[row.key] = {
          unit: incoming?.unit || row.unit,
          rate: incoming?.rate ?? 0,
          label: incoming?.label || row.label,
        };
      });
      setRates(merged);
    } catch (e) {
      setCard(null);
    } finally {
      setLoading(false);
    }
  }, [api, projectId]);

  useEffect(() => { load(); }, [load]);

  // Clear the "Saved" indicator whenever the user navigates to a
  // different project (so it doesn't linger after a switch).
  useEffect(() => { setSaveOk(false); }, [projectId]);

  const updateRate = (key, value) => {
    const numeric = Number(value);
    setRates(prev => ({
      ...prev,
      [key]: { ...prev[key], rate: Number.isFinite(numeric) ? numeric : 0 },
    }));
    setSaveOk(false);
  };

  const save = async () => {
    if (!projectId) return;
    setSaving(true);
    try {
      const payload = {};
      DEPT_ROWS.forEach(row => {
        const r = rates[row.key];
        if (r && Number(r.rate) > 0) {
          payload[row.key] = { rate: Number(r.rate), unit: r.unit, label: r.label };
        }
      });
      // PUT returns the full updated rate-card document. Use the
      // response directly instead of re-fetching — this avoids the
      // saveOk → load() → saveOk=false race that previously made
      // the green 'Saved' indicator disappear before the user could
      // see it (testing iter-47 RCA).
      const res = await api.put(`/projects/${projectId}/rate-card`, { rates: payload });
      setCard({ ...res.data, is_default: false });
      setSaveOk(true);
      // Auto-fade the indicator after 3s.
      setTimeout(() => setSaveOk(false), 3000);
    } catch (e) {
      alert(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleLock = async () => {
    if (!projectId || !card) return;
    setLockBusy(true);
    try {
      const path = card.locked ? 'unlock' : 'lock';
      await api.post(`/projects/${projectId}/rate-card/${path}`, {});
      await load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Lock toggle failed');
    } finally {
      setLockBusy(false);
    }
  };

  const isLocked = !!card?.locked;
  const canEdit = isAdmin && !isLocked;
  const grandTotal = Object.values(rates).reduce((s, r) => s + Number(r.rate || 0), 0);

  const grouped = DEPT_ROWS.reduce((acc, row) => {
    (acc[row.group] = acc[row.group] || []).push(row);
    return acc;
  }, {});

  return (
    <div className="space-y-5 p-6 max-w-5xl mx-auto" data-testid="project-rate-card-page">
      <div className="flex items-center gap-3">
        <Link
          to="/production"
          className="h-8 w-8 flex items-center justify-center bg-zinc-900 hover:bg-zinc-800 text-zinc-400 rounded-md"
          data-testid="rate-card-back"
        >
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <div className="flex-1">
          <h1 className="text-2xl font-heading font-bold text-white tracking-tight flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-emerald-400" />
            Project Rate Card
          </h1>
          <p className="text-xs text-zinc-500 mt-1">
            Lock a price-per-department for this project. Once locked, every Work Order auto-populates from this card and your allotments — no manual re-entry.
          </p>
        </div>
        <select
          value={projectId}
          onChange={e => setProjectId(e.target.value)}
          data-testid="rate-card-project-select"
          className="h-10 px-3 bg-zinc-900 border border-zinc-700 rounded-md text-xs text-zinc-200"
        >
          <option value="">Pick a project…</option>
          {projects.map(p => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
      </div>

      {/* ── LOCK BANNER ─── */}
      {card && (
        <div
          className={`flex items-center gap-3 px-4 py-3 rounded-md border ${isLocked
            ? 'bg-amber-500/10 border-amber-500/40 text-amber-200'
            : 'bg-emerald-500/10 border-emerald-500/30 text-emerald-200'}`}
          data-testid="rate-card-lock-banner"
        >
          {isLocked ? <Lock className="w-4 h-4 shrink-0" /> : <Unlock className="w-4 h-4 shrink-0" />}
          <div className="flex-1 text-xs">
            {isLocked ? (
              <>
                <span className="font-bold">Rate card is LOCKED.</span>{' '}
                <span className="opacity-70">
                  Locked {card.locked_at ? `at ${new Date(card.locked_at).toLocaleString()}` : 'recently'}
                  {card.locked_by_email ? ` by ${card.locked_by_email}` : ''}.
                  {isMasterAdmin ? ' Only a master admin (you) can unlock.' : ' Ask the master admin to unlock before editing.'}
                </span>
              </>
            ) : card.is_default ? (
              <>
                <span className="font-bold">Default rates loaded.</span>{' '}
                <span className="opacity-70">
                  Customise below for this project, then click Save. Lock the card once you're happy with the prices.
                </span>
              </>
            ) : (
              <>
                <span className="font-bold">Rate card is UNLOCKED.</span>{' '}
                <span className="opacity-70">
                  PM-editable. Lock when stable so new Work Orders bill from this card consistently.
                </span>
              </>
            )}
          </div>
          {isMasterAdmin && card && !card.is_default && (
            <button
              onClick={toggleLock}
              disabled={lockBusy}
              data-testid="rate-card-lock-toggle"
              className={`h-8 px-3 text-[11px] font-bold uppercase tracking-wider rounded-md flex items-center gap-1.5 ${isLocked
                ? 'bg-amber-400 hover:bg-amber-300 text-zinc-950'
                : 'bg-zinc-800 hover:bg-zinc-700 text-zinc-200'}`}
            >
              {lockBusy
                ? <Loader2 className="w-3 h-3 animate-spin" />
                : isLocked
                  ? <Unlock className="w-3 h-3" />
                  : <Lock className="w-3 h-3" />}
              {isLocked ? 'Unlock' : 'Lock card'}
            </button>
          )}
        </div>
      )}

      {/* ── RATE TABLE ─── */}
      {loading ? (
        <div className="p-16 flex items-center justify-center bg-zinc-900 border border-zinc-800 rounded-md"
             data-testid="rate-card-loading">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-400" />
        </div>
      ) : !projectId ? (
        <div className="p-16 text-center bg-zinc-900 border border-zinc-800 rounded-md text-zinc-500 text-sm">
          Pick a project above to view or edit its rate card.
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([group, rows]) => (
            <div
              key={group}
              className="bg-zinc-900 border border-zinc-800 rounded-md overflow-hidden"
              data-testid={`rate-card-group-${group.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <div className="px-4 py-2 bg-zinc-950/60 border-b border-zinc-800">
                <span className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold">
                  {group}
                </span>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-900/60">
                    <th className="px-4 py-2 text-left">Department</th>
                    <th className="px-4 py-2 text-center w-24">Unit</th>
                    <th className="px-4 py-2 text-right w-40">Rate (₹)</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <tr
                      key={row.key}
                      className="border-t border-zinc-800/40 hover:bg-zinc-800/20"
                      data-testid={`rate-card-row-${row.key}`}
                    >
                      <td className="px-4 py-2 text-zinc-200">
                        {rates[row.key]?.label || row.label}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <span className="text-[10px] uppercase tracking-wider text-zinc-500 bg-zinc-800/60 px-2 py-0.5 rounded">
                          per {row.unit}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <span className="text-zinc-500 text-[11px]">₹</span>
                          <input
                            type="number"
                            min="0"
                            step="50"
                            disabled={!canEdit}
                            value={rates[row.key]?.rate || 0}
                            onChange={e => updateRate(row.key, e.target.value)}
                            data-testid={`rate-card-input-${row.key}`}
                            className="h-8 w-28 px-2 bg-zinc-950 border border-zinc-700 rounded text-right text-zinc-100 font-mono text-xs disabled:opacity-60 disabled:cursor-not-allowed focus:border-emerald-500 outline-none"
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}

          {/* ── SAVE BAR ─── */}
          <div className="sticky bottom-0 bg-zinc-950/95 backdrop-blur border-t border-zinc-800 px-4 py-3 flex items-center gap-3"
               data-testid="rate-card-save-bar">
            <div className="flex-1 text-xs text-zinc-400">
              <span className="text-zinc-500">Sum of all department rates:</span>{' '}
              <span className="text-emerald-300 font-bold font-mono">₹{grandTotal.toLocaleString('en-IN')}</span>
              <span className="ml-3 text-[10px] text-zinc-600">
                (This is just the sum of unit rates — actual WO totals depend on quantities allotted.)
              </span>
            </div>
            {saveOk && (
              <span className="text-emerald-400 text-[11px] flex items-center gap-1" data-testid="rate-card-saved-flag">
                <Check className="w-3 h-3" /> Saved
              </span>
            )}
            <button
              onClick={save}
              disabled={!canEdit || saving}
              data-testid="rate-card-save-btn"
              className="h-9 px-4 bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-xs font-bold uppercase tracking-wider rounded-md flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save Rate Card
            </button>
          </div>

          {!canEdit && !isLocked && (
            <div className="text-xs text-amber-300 flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
              <AlertTriangle className="w-3.5 h-3.5" />
              You don't have permission to edit this card. Read-only view.
            </div>
          )}

          {/* Tip about auto-populate */}
          <div className="text-[11px] text-zinc-500 flex items-start gap-2 bg-zinc-900/60 border border-zinc-800 rounded-md px-3 py-2">
            <Sparkles className="w-3.5 h-3.5 text-violet-400 mt-0.5 shrink-0" />
            <span>
              When you build a Work Order, click <span className="text-violet-300 font-semibold">"Auto-populate from allotments"</span>.
              Every shot/asset already assigned to that vendor will be filled in using these rates,
              skipping anything already billed on a prior WO. You just review and send.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
